import { API } from "@swivl-mono/api-helper";
import { CRMUser } from "@swivl-mono/types";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { formatUserDisplayName } from "../../Helpers/Formatting/formatUserDisplayName";
import { Member } from "../../Models/Member/Member.Current";
import { SelectedCustomer } from "./CustomerPickerModal";

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 100;

interface UseCustomerPickerInput {
  open: boolean;
  onClose: () => void;
  onSelect: (customer: SelectedCustomer) => void;
  selectedCustomer?: SelectedCustomer | null;
  onRemove?: () => void;
  pageSize?: number;
}

interface UseCustomerPickerOutput {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  users: CRMUser[];
  loading: boolean;
  loadingMore: boolean;
  resolvedCustomer: SelectedCustomer | null;
  selectedUserSubtitle: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  handleNext: () => void;
  handlePrev: () => void;
  loadMore: () => void;
  handleRemove: () => void;
  handleSelectUser: (user: CRMUser) => void;
  userMap: Map<number, CRMUser>;
}

export function useCustomerPicker({
  open,
  onClose,
  onSelect,
  selectedCustomer,
  onRemove,
  pageSize = DEFAULT_PAGE_SIZE
}: UseCustomerPickerInput): UseCustomerPickerOutput {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<CRMUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchUsers = useCallback(async (searchTerm: string, cursor?: { after?: string; before?: string }) => {
    setLoading(true);
    try {
      const res = await API.CRMUsers.getCRMUsers(Member.orgId, {
        search: searchTerm || undefined,
        limit: pageSize,
        after: cursor?.after,
        before: cursor?.before,
        include: "locations"
      });
      setUsers(res.data ?? []);
      setHasNext(res.pagination.hasNext);
      setHasPrev(res.pagination.hasPrev);
      setNextCursor(res.pagination.nextCursor);
      setPrevCursor(res.pagination.prevCursor);
    } catch {
      setUsers([]);
      setHasNext(false);
      setHasPrev(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Resolved customer: starts from prop, enriched with CRM data when phone/location are missing
  const [resolvedCustomer, setResolvedCustomer] = useState<SelectedCustomer | null>(null);

  // Initialize / reset on open/close
  useEffect(() => {
    if (!open) {
      setSearch("");
      setUsers([]);
      setHasNext(false);
      setHasPrev(false);
      setNextCursor(null);
      setPrevCursor(null);
      setResolvedCustomer(null);
      return;
    }

    void fetchUsers("");
    setSearch("");
  }, [open, fetchUsers]);

  // Resolve selected customer — enrich with CRM data when phone/location are missing
  useEffect(() => {
    if (!open || !selectedCustomer) {
      setResolvedCustomer(null);
      return;
    }

    if (selectedCustomer.phone || selectedCustomer.locationName) {
      setResolvedCustomer(selectedCustomer);
      return;
    }

    let cancelled = false;
    const resolve = async (): Promise<void> => {
      try {
        const user = await API.CRMUsers.getCRMUserById(Member.orgId, selectedCustomer.id, "locations");

        if (cancelled) {
          return;
        }

        setResolvedCustomer({
          ...selectedCustomer,
          phone: user.phoneNumbers?.[0] ?? null,
          locationName: user.locations?.[0]?.name ?? null
        });
      } catch {
        if (!cancelled) {
          setResolvedCustomer(selectedCustomer);
        }
      }
    };
    void resolve();
    return () => {
      cancelled = true;
    };
  }, [open, selectedCustomer]);

  // Debounced search — skip empty search on open since the initial-load effect handles it
  const initialOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initialOpenRef.current = false;
      return;
    }
    if (!initialOpenRef.current) {
      initialOpenRef.current = true;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fetchUsers(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, open, fetchUsers]);

  const handleNext = useCallback((): void => {
    if (nextCursor) void fetchUsers(search, { after: nextCursor });
  }, [nextCursor, search, fetchUsers]);

  const handlePrev = useCallback((): void => {
    if (prevCursor) void fetchUsers(search, { before: prevCursor });
  }, [prevCursor, search, fetchUsers]);

  const loadMore = useCallback((): void => {
    if (!nextCursor || loadingMore || loading) return;
    setLoadingMore(true);
    API.CRMUsers.getCRMUsers(Member.orgId, {
      search: search || undefined,
      limit: pageSize,
      after: nextCursor,
      include: "locations"
    })
      .then((res) => {
        setUsers((prev) => [...prev, ...(res.data ?? [])]);
        setHasNext(res.pagination.hasNext);
        setNextCursor(res.pagination.nextCursor);
      })
      .catch(() => {
        setHasNext(false);
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }, [nextCursor, loadingMore, loading, search]);

  const userMap = useMemo(() => {
    const map = new Map<number, CRMUser>();
    for (const user of users) {
      map.set(user.id, user);
    }
    return map;
  }, [users]);

  const handleRemove = useCallback((): void => {
    onRemove?.();
    onClose();
  }, [onRemove, onClose]);

  const selectedUserSubtitle = useMemo(() => {
    if (!resolvedCustomer) return null;
    const parts = [resolvedCustomer.phone, resolvedCustomer.locationName].filter(Boolean);
    return parts.length > 0 ? parts.join(" | ") : null;
  }, [resolvedCustomer]);

  const handleSelectUser = useCallback(
    (user: CRMUser): void => {
      onSelect({
        id: user.id,
        name: formatUserDisplayName(user.id, user.name),
        phone: user.phoneNumbers?.[0] ?? null,
        locationName: user.locations?.[0]?.name ?? null
      });
      onClose();
    },
    [onSelect, onClose]
  );

  return {
    search,
    setSearch,
    users,
    loading,
    loadingMore,
    resolvedCustomer,
    selectedUserSubtitle,
    hasNext,
    hasPrev,
    handleNext,
    handlePrev,
    loadMore,
    handleRemove,
    handleSelectUser,
    userMap
  };
}
